import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useContent, useLanguage, useUI, useLocalPath } from '../context/LanguageContext';

const ProjectsPreview = () => {
  const content = useContent();
  const localPath = useLocalPath();
  const ui = useUI();
  const featured = content.projects.slice(0, 3);

  return (
    <section className="relative py-32 border-t border-[#21262d]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-10 mb-16">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-mono text-[11px] tracking-[0.2em] text-[#3b82f6] uppercase block mb-4">
                {ui.ourPlatformsLabel}
              </span>
              <h2 className="text-[clamp(36px,4.5vw,64px)] font-black leading-[0.95] tracking-[-0.02em]">
                <span className="text-[#e6edf3]">{ui.weBuildTitle[0]}</span>
                <span className="text-[#7d8590]">{ui.weBuildTitle[1]}</span>
              </h2>
              <p className="font-mono text-[12px] leading-[1.7] text-[#7d8590] mt-6 max-w-md">
                {ui.projectsDescription}
              </p>
            </motion.div>
          </div>

          <div className="flex items-end justify-end gap-10">
            <div className="text-right">
              <span className="text-3xl font-bold text-[#3b82f6]">500K+</span>
              <p className="font-mono text-[10px] tracking-[0.1em] text-[#7d8590] mt-1 uppercase">{ui.monthlyVisits}</p>
            </div>
            <div className="text-right">
              <span className="text-3xl font-bold text-[#e6edf3]">5</span>
              <p className="font-mono text-[10px] tracking-[0.1em] text-[#7d8590] mt-1 uppercase">{ui.livePlatforms}</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          {featured.map((project, i) => (
            <motion.div
              key={project.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group bg-[#161b22] border border-[#21262d] rounded-lg p-6 hover:border-[#3b82f6]/30 transition-all duration-500 flex flex-col"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[11px] text-[#7d8590]">{project.year}</span>
                  <span className={`font-mono text-[10px] tracking-[0.1em] px-2.5 py-0.5 rounded-full uppercase ${
                    project.status === 'In Development' || project.status === 'V razvoju'
                      ? 'text-yellow-400 bg-yellow-400/10'
                      : 'text-[#3b82f6] bg-[#3b82f6]/10'
                  }`}>
                    {project.status}
                  </span>
                </div>
                {project.link && (
                  <a href={project.link} target="_blank" rel="noopener noreferrer" className="text-[#7d8590] hover:text-[#3b82f6] transition-colors">
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>

              <h3 className="text-lg font-bold text-[#e6edf3] mb-1 group-hover:text-[#3b82f6] transition-colors duration-300">
                {project.name}
              </h3>
              <p className="font-mono text-[10px] tracking-[0.1em] text-[#3b82f6] mb-3 uppercase">{project.type}</p>
              <p className="font-mono text-[11px] leading-[1.7] text-[#7d8590] mb-5 flex-1">
                {project.tagline}
              </p>

              <div className="flex flex-wrap gap-2">
                {project.tags.map((tag) => (
                  <span
                    key={tag}
                    className="font-mono text-[10px] tracking-[0.05em] text-[#7d8590] border border-[#21262d] px-2.5 py-1 rounded"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-12 text-center"
        >
          <Link
            to={localPath('/projects/')}
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.1em] uppercase text-[#3b82f6] border border-[#3b82f6]/50 rounded-full px-6 py-2.5 hover:bg-[#3b82f6]/10 transition-all duration-300"
          >
            {ui.viewAllPlatforms}
            <ArrowRight size={13} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ProjectsPreview;
